import {
  Group,
  ActionIcon,
  Menu,
  Avatar,
  Indicator,
  Text,
  Image,
  Box,
  useMantineColorScheme,
  useComputedColorScheme,
} from '@mantine/core';
import {
  IconBell,
  IconUser,
  IconSettings,
  IconLogout,
  IconSun,
  IconMoon,
} from '@tabler/icons-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { usePortalAuthStore } from '../stores/portalAuthStore';
import { getPortalDisplayName, getPortalInitials } from '../types/portal';

export function PortalHeader() {
  const { portalUser, practiceInfo, logout } = usePortalAuthStore();
  const navigate = useNavigate();
  const { setColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme('light');
  const isDark = computedColorScheme === 'dark';

  const userInitials = portalUser ? getPortalInitials(portalUser) : '?';

  const handleLogout = () => {
    logout();
    void navigate({ to: '/portal/login' });
  };

  return (
    <Box
      component='header'
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 200,
        height: 56,
        minHeight: 56,
        backgroundColor: 'var(--mantine-color-body)',
        borderBottom: '1px solid var(--mantine-color-default-border)',
      }}
    >
      <Group
        h={56}
        px='md'
        justify='space-between'
        wrap='nowrap'
        style={{ maxWidth: 640, margin: '0 auto' }}
      >
        {/* Left: Practice logo / name */}
        <Link to='/portal/dashboard' style={{ textDecoration: 'none' }}>
          <Group gap='xs' wrap='nowrap'>
            {practiceInfo?.logo ? (
              <Image
                src={practiceInfo.logo}
                alt={practiceInfo.name}
                h={32}
                w='auto'
                fit='contain'
              />
            ) : (
              <Text fw={700} size='lg' c={practiceInfo?.themeColor ?? 'brand'} truncate>
                {practiceInfo?.name ?? 'Client Portal'}
              </Text>
            )}
          </Group>
        </Link>

        {/* Right: Theme toggle + Notification bell + Avatar menu */}
        <Group gap={4} wrap='nowrap'>
          {/* Theme toggle */}
          <ActionIcon
            variant='subtle'
            color='gray'
            size={40}
            radius='xl'
            onClick={() => setColorScheme(isDark ? 'light' : 'dark')}
            aria-label='Toggle theme'
          >
            {isDark ? <IconSun size={20} /> : <IconMoon size={20} />}
          </ActionIcon>

          {/* Notification bell */}
          <Indicator size={8} color='red' offset={4} disabled>
            <ActionIcon
              component={Link}
              to='/portal/notifications'
              variant='subtle'
              color='gray'
              size={44}
              radius='xl'
              aria-label='Notifications'
            >
              <IconBell size={22} />
            </ActionIcon>
          </Indicator>

          {/* Avatar dropdown */}
          <Menu shadow='md' width={200} position='bottom-end' withArrow>
            <Menu.Target>
              <ActionIcon
                variant='subtle'
                color='gray'
                size={44}
                radius='xl'
                aria-label='User menu'
              >
                <Avatar
                  size={32}
                  radius='xl'
                  color='brand'
                  src={portalUser?.avatarUrl ?? undefined}
                >
                  {userInitials}
                </Avatar>
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label style={{ lineHeight: 1.35 }}>
                {getPortalDisplayName(portalUser) ?? 'Portal user'}
              </Menu.Label>
              <Menu.Item
                leftSection={<IconUser size={16} />}
                component={Link}
                to='/portal/settings'
              >
                Profile
              </Menu.Item>
              <Menu.Item
                leftSection={<IconSettings size={16} />}
                component={Link}
                to='/portal/settings'
              >
                Settings
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item leftSection={<IconLogout size={16} />} color='red' onClick={handleLogout}>
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>
    </Box>
  );
}
